import { motion } from 'framer-motion';
import {
  AlertTriangle,
  CheckCircle,
  Edit3,
  FileText,
  PieChart,
  Shield,
  TrendingUp,
  User,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { ToolLayout } from '@layouts/ToolLayout';

import { Button } from '@ui/Button';
import { WhatsAppCTA } from '@ui/WhatsAppCTA';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

interface Question {
  id: string;
  question: string;
  options: {
    value: string;
    label: string;
    score: number;
    description?: string;
  }[];
}

interface RiskProfile {
  type: string;
  title: string;
  description: string;
  characteristics: string[];
  recommendedAllocation: {
    equity: number;
    debt: number;
    gold: number;
  };
  expectedReturns: {
    min: number;
    max: number;
  };
  timeHorizon: string;
  color: string;
  conservativeReasons?: string[];
}

interface RiskMeterProps {
  riskLevel: number; // 1-5 scale
  weight?: number; // Weight multiplier
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  showWeight?: boolean;
}

const RiskMeter: React.FC<RiskMeterProps> = ({
  riskLevel,
  weight = 1,
  size = 'md',
  showLabel = true,
  showWeight = false,
}) => {
  const getRiskColor = (level: number) => {
    if (level <= 1.5) return COLORS.riskConservative; // Conservative
    if (level <= 2.5) return COLORS.riskCautious; // Cautious
    if (level <= 3.5) return COLORS.riskBalanced; // Balanced
    if (level <= 4.5) return COLORS.riskGrowth; // Growth-Focused
    return COLORS.riskAggressive; // Aggressive Growth
  };

  const getRiskLabel = (level: number, isMobile = false) => {
    if (level <= 1.5) return isMobile ? 'Low' : 'Conservative';
    if (level <= 2.5) return isMobile ? 'Mild' : 'Cautious';
    if (level <= 3.5) return isMobile ? 'Medium' : 'Balanced';
    if (level <= 4.5) return isMobile ? 'High' : 'Growth-Focused';
    return isMobile ? 'Very high' : 'Aggressive Growth';
  };

  const sizeClasses = {
    sm: 'h-2 w-24',
    md: 'h-3 w-32',
    lg: 'h-4 w-40',
  };

  const dotSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const riskColor = getRiskColor(riskLevel);
  const riskLabel = getRiskLabel(riskLevel);
  const riskLabelMobile = getRiskLabel(riskLevel, true);
  const percentage = (riskLevel / 5) * 100;

  return (
    <div className="flex items-center space-x-2 sm:space-x-3">
      <div className="flex-1 min-w-0">
        <div
          className={`relative bg-gray-200 rounded-full ${sizeClasses[size]}`}
        >
          {/* Risk level fill */}
          <div
            className="absolute top-0 left-0 h-full rounded-full transition-all duration-300"
            style={{
              width: `${percentage}%`,
              backgroundColor: riskColor,
            }}
          />
          {/* Risk level indicator dot */}
          <div
            className={`absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 ${dotSizes[size]} rounded-full border-2 border-white shadow-md transition-all duration-300`}
            style={{
              left: `${percentage}%`,
              backgroundColor: riskColor,
            }}
          />
        </div>
      </div>

      {showLabel && (
        <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
          <span
            className="text-xs sm:text-sm font-medium whitespace-nowrap"
            style={{ color: riskColor }}
          >
            <span className="hidden sm:inline">{riskLabel}</span>
            <span className="sm:hidden">{riskLabelMobile}</span>
          </span>
          {showWeight && weight !== 1 && (
            <span className="text-xs text-gray-500 bg-gray-100 px-1 sm:px-2 py-1 rounded-full whitespace-nowrap">
              {weight}x
            </span>
          )}
        </div>
      )}
    </div>
  );
};

type QuestionBadgeProps = {
  number: number;
  isAnswered: boolean;
  size?: 'sm' | 'md';
};

const QuestionBadge: React.FC<QuestionBadgeProps> = ({
  number,
  isAnswered,
  size = 'md',
}) => {
  const sizeClasses = {
    sm: 'w-6 h-6 sm:w-7 sm:h-7 text-xs sm:text-sm mr-2',
    md: 'w-7 h-7 sm:w-8 sm:h-8 text-sm sm:text-base mr-2 sm:mr-3',
  };

  return (
    <span
      className={`inline-flex items-center justify-center rounded-lg font-bold ${sizeClasses[size]} ${
        isAnswered
          ? 'font-serif bg-green-100 text-green-700 border-2 border-green-500'
          : 'font-sans bg-gray-100 text-gray-600 border-2 border-gray-300'
      }`}
    >
      {isAnswered ? '✓' : number}
    </span>
  );
};

const questions: Question[] = [
  {
    id: 'age',
    question: 'What is your age group?',
    options: [
      {
        value: 'under-25',
        label: 'Under 25 years',
        score: 5,
        description: 'Long investment horizon',
      },
      {
        value: '25-35',
        label: '25-35 years',
        score: 4,
        description: 'Good time for wealth building',
      },
      {
        value: '35-45',
        label: '35-45 years',
        score: 3,
        description: 'Peak earning years',
      },
      {
        value: '45-55',
        label: '45-55 years',
        score: 2,
        description: 'Pre-retirement planning',
      },
      {
        value: 'above-55',
        label: 'Above 55 years',
        score: 1,
        description: 'Near or in retirement',
      },
    ],
  },
  {
    id: 'income',
    question: 'What is your monthly household income?',
    options: [
      {
        value: 'below-25k',
        label: 'Below ₹25,000',
        score: 1,
        description: 'Focus on stability',
      },
      {
        value: '25k-50k',
        label: '₹25,000 - ₹50,000',
        score: 2,
        description: 'Building financial base',
      },
      {
        value: '50k-1l',
        label: '₹50,000 - ₹1,00,000',
        score: 3,
        description: 'Good investment capacity',
      },
      {
        value: '1l-2l',
        label: '₹1,00,000 - ₹2,00,000',
        score: 4,
        description: 'Strong investment potential',
      },
      {
        value: 'above-2l',
        label: 'Above ₹2,00,000',
        score: 5,
        description: 'High investment capacity',
      },
    ],
  },
  {
    id: 'experience',
    question: 'How much investment experience do you have?',
    options: [
      {
        value: 'beginner',
        label: 'Beginner (0-1 years)',
        score: 1,
        description: 'New to investing',
      },
      {
        value: 'basic',
        label: 'Basic (1-3 years)',
        score: 2,
        description: 'Some experience',
      },
      {
        value: 'intermediate',
        label: 'Intermediate (3-5 years)',
        score: 3,
        description: 'Moderate experience',
      },
      {
        value: 'experienced',
        label: 'Experienced (5-10 years)',
        score: 4,
        description: 'Good understanding',
      },
      {
        value: 'expert',
        label: 'Expert (10+ years)',
        score: 5,
        description: 'Extensive experience',
      },
    ],
  },
  {
    id: 'goal_timeline',
    question: 'What is your primary investment timeline?',
    options: [
      {
        value: 'short',
        label: 'Short term (1-3 years)',
        score: 1,
        description: 'Need money soon',
      },
      {
        value: 'medium',
        label: 'Medium term (3-7 years)',
        score: 3,
        description: 'Moderate timeline',
      },
      {
        value: 'long',
        label: 'Long term (7-15 years)',
        score: 4,
        description: 'Good growth period',
      },
      {
        value: 'very-long',
        label: 'Very long term (15+ years)',
        score: 5,
        description: 'Maximum growth potential',
      },
    ],
  },
  {
    id: 'market_reaction',
    question:
      'If your investment loses 20% value in a month, what would you do?',
    options: [
      {
        value: 'panic-sell',
        label: 'Sell immediately to avoid further losses',
        score: 1,
        description: 'Risk averse behavior',
      },
      {
        value: 'worry-hold',
        label: 'Worry but hold the investment',
        score: 2,
        description: 'Moderate concern',
      },
      {
        value: 'neutral',
        label: 'Stay calm and review the situation',
        score: 3,
        description: 'Balanced approach',
      },
      {
        value: 'buy-more',
        label: 'Buy more at lower prices',
        score: 4,
        description: 'Opportunistic mindset',
      },
      {
        value: 'ignore',
        label: 'Ignore and continue investing',
        score: 5,
        description: 'High risk tolerance',
      },
    ],
  },
  {
    id: 'emergency_fund',
    question: 'Do you have an emergency fund covering 6-12 months of expenses?',
    options: [
      {
        value: 'no-fund',
        label: 'No emergency fund',
        score: 1,
        description: 'Need to build safety first',
      },
      {
        value: 'partial',
        label: '1-3 months covered',
        score: 2,
        description: 'Partial safety net',
      },
      {
        value: 'adequate',
        label: '3-6 months covered',
        score: 3,
        description: 'Good safety net',
      },
      {
        value: 'excellent',
        label: '6-12 months covered',
        score: 4,
        description: 'Strong safety net',
      },
      {
        value: 'surplus',
        label: 'More than 12 months covered',
        score: 5,
        description: 'Excellent safety net',
      },
    ],
  },
  {
    id: 'debt_status',
    question: 'What is your current debt situation?',
    options: [
      {
        value: 'high-debt',
        label: 'High debt (>50% of income)',
        score: 1,
        description: 'Focus on debt reduction',
      },
      {
        value: 'moderate-debt',
        label: 'Moderate debt (20-50% of income)',
        score: 2,
        description: 'Manageable debt levels',
      },
      {
        value: 'low-debt',
        label: 'Low debt (<20% of income)',
        score: 3,
        description: 'Good debt management',
      },
      {
        value: 'only-home-loan',
        label: 'Only home loan/education loan',
        score: 4,
        description: 'Good debt only',
      },
      {
        value: 'debt-free',
        label: 'Debt free',
        score: 5,
        description: 'Excellent financial position',
      },
    ],
  },
  {
    id: 'investment_priority',
    question: 'What is your primary investment goal?',
    options: [
      {
        value: 'capital-protection',
        label: 'Capital protection',
        score: 1,
        description: 'Safety first approach',
      },
      {
        value: 'steady-income',
        label: 'Steady income generation',
        score: 2,
        description: 'Regular returns focus',
      },
      {
        value: 'balanced-growth',
        label: 'Balanced growth with some income',
        score: 3,
        description: 'Moderate growth',
      },
      {
        value: 'wealth-creation',
        label: 'Long-term wealth creation',
        score: 4,
        description: 'Growth focused',
      },
      {
        value: 'aggressive-growth',
        label: 'Aggressive wealth building',
        score: 5,
        description: 'Maximum growth',
      },
    ],
  },
];

const riskProfiles: Record<string, RiskProfile> = {
  conservative: {
    type: 'conservative',
    title: 'Conservative investor',
    description:
      'You prioritize capital protection and stability. Your focus is on preserving wealth with minimal risk and steady, predictable returns.',
    characteristics: [
      'Capital preservation focused',
      'Prefers guaranteed returns',
      'Low volatility tolerance',
      'Suitable for short-term goals',
      'Ideal for retirees or near-retirement',
    ],
    recommendedAllocation: {
      equity: 20,
      debt: 70,
      gold: 10,
    },
    expectedReturns: {
      min: 7,
      max: 9,
    },
    timeHorizon: '1-3 years',
    color: COLORS.sage,
  },
  cautious: {
    type: 'cautious',
    title: 'Cautious investor',
    description:
      'You prefer stability but are open to some growth. You want steady returns with limited downside risk.',
    characteristics: [
      'Safety-first approach',
      'Accepts minimal volatility',
      'Focuses on steady income',
      'Good for near-term goals',
      'Suitable for conservative savers',
    ],
    recommendedAllocation: {
      equity: 35,
      debt: 55,
      gold: 10,
    },
    expectedReturns: {
      min: 8,
      max: 10,
    },
    timeHorizon: '2-5 years',
    color: '#f59e0b', // Yellow
  },
  balanced: {
    type: 'balanced',
    title: 'Balanced investor',
    description:
      'You seek a balance between growth and stability. You can tolerate moderate volatility for better long-term returns.',
    characteristics: [
      'Balanced risk approach',
      'Seeks steady growth',
      'Can handle moderate volatility',
      'Good for medium-term goals',
      'Suitable for most investors',
    ],
    recommendedAllocation: {
      equity: 60,
      debt: 30,
      gold: 10,
    },
    expectedReturns: {
      min: 10,
      max: 12,
    },
    timeHorizon: '3-7 years',
    color: COLORS.gold,
  },
  growthFocused: {
    type: 'growthFocused',
    title: 'Growth-focused investor',
    description:
      'You prioritize long-term wealth creation and can handle significant volatility. You focus on growth over immediate income.',
    characteristics: [
      'Growth-oriented mindset',
      'High volatility tolerance',
      'Long-term wealth building',
      'Comfortable with market swings',
      'Ideal for wealth accumulation phase',
    ],
    recommendedAllocation: {
      equity: 80,
      debt: 15,
      gold: 5,
    },
    expectedReturns: {
      min: 12,
      max: 15,
    },
    timeHorizon: '5-10 years',
    color: '#f97316', // Orange-Red
  },
  aggressive: {
    type: 'aggressive',
    title: 'Aggressive investor',
    description:
      'You are willing to take maximum risks for potentially highest returns. You have a very long investment horizon and strong risk appetite.',
    characteristics: [
      'Maximum risk tolerance',
      'Seeks highest growth potential',
      'Can handle extreme volatility',
      'Perfect for very long-term goals',
      'Ideal for young, high-income investors',
    ],
    recommendedAllocation: {
      equity: 90,
      debt: 5,
      gold: 5,
    },
    expectedReturns: {
      min: 14,
      max: 18,
    },
    timeHorizon: '10+ years',
    color: COLORS.accent,
  },
};

// Weighted scoring system - critical questions have more impact
const weights = {
  age: 2.0, // Age is crucial for time horizon
  income: 1.2, // Income affects capacity
  experience: 1.0, // Experience is important but not critical
  goal_timeline: 2.5, // Timeline is the most critical factor
  market_reaction: 2.0, // Risk tolerance is crucial
  emergency_fund: 1.5, // Safety net affects risk capacity
  debt_status: 1.3, // Debt situation affects capacity
  investment_priority: 2.0, // Investment goal is very important
};

export const RiskProfiler = () => {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [riskProfile, setRiskProfile] = useState<RiskProfile | null>(null);
  const [totalScore, setTotalScore] = useState(0);
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);

  const calculateRiskProfile = useCallback(() => {
    let weightedScore = 0;
    let maxPossibleScore = 0;
    const conservativeReasons: string[] = [];

    Object.entries(answers).forEach(([questionId, answerValue]) => {
      const question = questions.find(q => q.id === questionId);
      if (question) {
        const option = question.options.find(opt => opt.value === answerValue);
        if (option) {
          const weight = weights[questionId as keyof typeof weights] || 1.0;
          weightedScore += option.score * weight;
          maxPossibleScore += 5 * weight; // Max score per question is 5
        }
      }
    });

    // Convert to percentage for easier interpretation
    const scorePercentage = (weightedScore / maxPossibleScore) * 100;
    setTotalScore(Math.round(scorePercentage));

    // Check for conservative-forcing conditions with explanations
    const timelineAnswer = answers['goal_timeline'];
    const marketReactionAnswer = answers['market_reaction'];
    const ageAnswer = answers['age'];
    const emergencyFundAnswer = answers['emergency_fund'];
    const debtAnswer = answers['debt_status'];
    const priorityAnswer = answers['investment_priority'];

    // Only CRITICAL combinations should force conservative classification
    // Single factors alone should not override the overall score

    // Critical Factor 1: Short timeline + Panic selling (double red flag)
    if (timelineAnswer === 'short' && marketReactionAnswer === 'panic-sell') {
      conservativeReasons.push(
        'Short investment timeline combined with panic-selling tendency requires conservative approach'
      );
    }

    // Critical Factor 2: Short timeline + No emergency fund (liquidity crisis risk)
    else if (timelineAnswer === 'short' && emergencyFundAnswer === 'no-fund') {
      conservativeReasons.push(
        'Short timeline with no emergency fund requires immediate liquidity and capital protection'
      );
    }

    // Critical Factor 3: High debt + Short timeline (financial stress)
    else if (debtAnswer === 'high-debt' && timelineAnswer === 'short') {
      conservativeReasons.push(
        'High debt levels with short timeline require focus on debt reduction over risky investments'
      );
    }

    // Critical Factor 4: Explicit capital protection goal (user's clear preference)
    else if (priorityAnswer === 'capital-protection') {
      conservativeReasons.push(
        'Primary goal of capital protection aligns with conservative investing approach'
      );
    }

    // Critical Factor 5: Near retirement with short/medium timeline
    else if (
      ageAnswer === 'above-55' &&
      (timelineAnswer === 'short' || timelineAnswer === 'medium')
    ) {
      conservativeReasons.push(
        'Near retirement age with shorter timeline requires wealth preservation over growth'
      );
    }

    // Only force conservative if we have CRITICAL combinations, not single factors
    if (conservativeReasons.length > 0) {
      setRiskProfile({
        ...riskProfiles.conservative,
        conservativeReasons,
      });
      return;
    }

    // Enhanced scoring ranges based on weighted percentage (5 profiles)
    if (scorePercentage <= 25) {
      setRiskProfile(riskProfiles.conservative);
    } else if (scorePercentage <= 40) {
      setRiskProfile(riskProfiles.cautious);
    } else if (scorePercentage <= 60) {
      setRiskProfile(riskProfiles.balanced);
    } else if (scorePercentage <= 80) {
      setRiskProfile(riskProfiles.growthFocused);
    } else {
      setRiskProfile(riskProfiles.aggressive);
    }
  }, [answers]);

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSubmit = () => {
    calculateRiskProfile();
    setShowResults(true);
    // Scroll to top to show results from beginning
    window.scrollTo(0, 0);
  };

  const startNewAssessment = () => {
    const confirmed = window.confirm(
      'Are you sure you want to clear all your answers and start a new assessment? This action cannot be undone.'
    );
    if (confirmed) {
      setAnswers({});
      setShowResults(false);
      setRiskProfile(null);
      setTotalScore(0);
    }
  };

  const answeredQuestions = Object.keys(answers).length;
  const allQuestionsAnswered = answeredQuestions === questions.length;
  const progress = (answeredQuestions / questions.length) * 100;

  useEffect(() => {
    if (showResults) {
      calculateRiskProfile();
    }
  }, [showResults, calculateRiskProfile, answers]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Sample portfolio calculation for ₹1 lakh investment
  const sampleInvestment = 100000;
  const calculateSamplePortfolio = (profile: RiskProfile) => {
    return {
      equity: (sampleInvestment * profile.recommendedAllocation.equity) / 100,
      debt: (sampleInvestment * profile.recommendedAllocation.debt) / 100,
      gold: (sampleInvestment * profile.recommendedAllocation.gold) / 100,
    };
  };

  // Generate personalized characteristics based on user's answers
  const generatePersonalizedCharacteristics = (
    profile: RiskProfile
  ): string[] => {
    const characteristics: string[] = [];

    const ageAnswer = answers['age'];
    const experienceAnswer = answers['experience'];
    const timelineAnswer = answers['goal_timeline'];
    const marketReactionAnswer = answers['market_reaction'];
    const emergencyFundAnswer = answers['emergency_fund'];
    const priorityAnswer = answers['investment_priority'];

    // Age-based characteristics
    if (ageAnswer === 'under-25' || ageAnswer === '25-35') {
      characteristics.push('Long investment horizon for wealth building');
    } else if (ageAnswer === '35-45') {
      characteristics.push('Peak earning years with good investment capacity');
    } else if (ageAnswer === '45-55') {
      characteristics.push('Pre-retirement planning phase');
    } else if (ageAnswer === 'above-55') {
      characteristics.push('Near or in retirement, focus on preservation');
    }

    // Risk tolerance based on market reaction
    if (marketReactionAnswer === 'panic-sell') {
      characteristics.push('Prefers avoiding market volatility');
    } else if (marketReactionAnswer === 'worry-hold') {
      characteristics.push('Moderate comfort with market fluctuations');
    } else if (marketReactionAnswer === 'neutral') {
      characteristics.push('Balanced approach to market volatility');
    } else if (
      marketReactionAnswer === 'buy-more' ||
      marketReactionAnswer === 'ignore'
    ) {
      characteristics.push('High tolerance for market volatility');
    }

    // Timeline-based characteristics
    if (timelineAnswer === 'short') {
      characteristics.push('Short-term goals require capital protection');
    } else if (timelineAnswer === 'medium') {
      characteristics.push('Medium-term goals allow moderate growth focus');
    } else if (timelineAnswer === 'long' || timelineAnswer === 'very-long') {
      characteristics.push('Long-term goals enable aggressive growth strategy');
    }

    // Emergency fund status
    if (
      emergencyFundAnswer === 'no-fund' ||
      emergencyFundAnswer === 'partial'
    ) {
      characteristics.push('Building emergency fund is a priority');
    } else if (
      emergencyFundAnswer === 'adequate' ||
      emergencyFundAnswer === 'excellent' ||
      emergencyFundAnswer === 'surplus'
    ) {
      characteristics.push('Strong financial safety net in place');
    }

    // Investment priority
    if (priorityAnswer === 'capital-protection') {
      characteristics.push('Values capital protection over high returns');
    } else if (priorityAnswer === 'steady-income') {
      characteristics.push('Focuses on regular income generation');
    } else if (priorityAnswer === 'balanced-growth') {
      characteristics.push('Seeks balanced growth with some income');
    } else if (
      priorityAnswer === 'wealth-creation' ||
      priorityAnswer === 'aggressive-growth'
    ) {
      characteristics.push('Focused on long-term wealth creation');
    }

    // Experience level
    if (experienceAnswer === 'beginner') {
      characteristics.push('New to investing, learning the basics');
    } else if (experienceAnswer === 'basic') {
      characteristics.push('Some investment experience, building confidence');
    } else if (
      experienceAnswer === 'intermediate' ||
      experienceAnswer === 'experienced' ||
      experienceAnswer === 'expert'
    ) {
      characteristics.push(
        'Experienced investor with good market understanding'
      );
    }

    return characteristics;
  };

  if (showResults && riskProfile) {
    const samplePortfolio = calculateSamplePortfolio(riskProfile);
    const personalizedCharacteristics =
      generatePersonalizedCharacteristics(riskProfile);

    return (
      <ToolLayout
        title={`Risk profiler | ${CONTACT_INFO.company.name}`}
        heroTitle="Risk profiler"
        description="Discover your investment personality and get personalized portfolio allocation recommendations"
        canonicalUrl="https://niveshright.com/tools/risk-profiler"
        backgroundColor={COLORS.mist}
        heroStyle="gradient"
        heroIcon={
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: `${riskProfile.color}20` }}
          >
            <Shield size={32} style={{ color: riskProfile.color }} />
          </div>
        }
        showBreadcrumb={true}
        keywords="risk profiler, investment risk assessment, portfolio allocation, risk tolerance, investment personality"
        cta={
          <WhatsAppCTA
            title="Ready to build your portfolio?"
            description="Get personalized investment advice based on your risk profile and start building wealth with expert guidance."
            phoneNumber={CONTACT_INFO.phone.whatsapp}
            message={`Hi ${CONTACT_INFO.contact_person.short_name}! I completed your risk profiler assessment with these results:

RISK ASSESSMENT RESULTS:
• Risk profile: ${riskProfile.title}
• Risk score: ${totalScore}% (weighted analysis)
• Investment timeline: ${riskProfile.timeHorizon}
• Expected returns: ${riskProfile.expectedReturns.min}%-${riskProfile.expectedReturns.max}%
${riskProfile.conservativeReasons && riskProfile.conservativeReasons.length > 0 ? `• Conservative classification due to: ${riskProfile.conservativeReasons[0]}` : ''}

RECOMMENDED PORTFOLIO ALLOCATION:
• Equity: ${riskProfile.recommendedAllocation.equity}%
• Debt: ${riskProfile.recommendedAllocation.debt}%
• Gold: ${riskProfile.recommendedAllocation.gold}%

I'd like personalized investment advice based on my risk assessment. Can you help me create a portfolio and suggest specific mutual funds that match my ${riskProfile.title.toLowerCase()} risk profile?`}
            buttonText="Get personalized advice"
            backgroundColor={COLORS.pearl}
            buttonColor={riskProfile.color}
          />
        }
      >
        {/* Full-Width Risk-O-Meter Assessment */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-8 mb-12 border border-blue-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Risk score
              </h2>
              <p className="text-gray-600">
                Weighted risk analysis based on {questions.length} factors with
                critical elements having higher impact
              </p>
            </div>
            <div className="text-right">
              <div
                className="text-4xl font-bold"
                style={{ color: riskProfile.color }}
              >
                {totalScore}%
              </div>
              <div className="text-sm text-gray-500">risk score</div>
              <div className="text-xs text-gray-500 mt-1">
                {riskProfile.title}
              </div>
            </div>
          </div>

          {/* Full-Width Risk Meter */}
          <div className="mb-6">
            <div className="relative rounded-full h-6 mb-4">
              {/* Smooth immersive gradient background */}
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: `linear-gradient(to right, ${COLORS.riskConservative} 0%, #84cc16 15%, ${COLORS.riskCautious} 30%, #f59e0b 45%, ${COLORS.riskBalanced} 60%, #ea580c 75%, ${COLORS.riskGrowth} 85%, ${COLORS.riskAggressive} 100%)`,
                }}
              ></div>

              {/* Risk level indicator dot */}
              <div
                className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full border-4 border-white shadow-lg transition-all duration-700 z-20"
                style={{
                  left: `${totalScore}%`,
                  backgroundColor: riskProfile.color,
                  boxShadow: `0 4px 12px ${riskProfile.color}60`,
                }}
              />
            </div>

            {/* Desktop Labels */}
            <div className="hidden sm:block">
              <div className="grid grid-cols-5 gap-1 text-sm text-gray-600 text-center mb-2">
                <span className="font-medium">Conservative</span>
                <span className="font-medium">Cautious</span>
                <span className="font-medium">Balanced</span>
                <span className="font-medium">Growth</span>
                <span className="font-medium">Aggressive</span>
              </div>
              <div className="grid grid-cols-5 gap-1 text-xs text-gray-500 text-center">
                <span>(0-25%)</span>
                <span>(26-40%)</span>
                <span>(41-60%)</span>
                <span>(61-80%)</span>
                <span>(81-100%)</span>
              </div>
            </div>

            {/* Mobile Legend */}
            <div className="sm:hidden mt-4">
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs">
                <div className="flex items-center space-x-1">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS.riskConservative }}
                  ></div>
                  <span className="text-gray-600">Conservative (0-25%)</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS.riskCautious }}
                  ></div>
                  <span className="text-gray-600">Cautious (26-40%)</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS.riskBalanced }}
                  ></div>
                  <span className="text-gray-600">Balanced (41-60%)</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS.riskGrowth }}
                  ></div>
                  <span className="text-gray-600">Growth (61-80%)</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS.riskAggressive }}
                  ></div>
                  <span className="text-gray-600">Aggressive (81-100%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Results Section */}
        <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-3xl p-8 mb-12 border border-blue-100">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Profile Identity */}
            <div className="lg:col-span-1">
              <div className="flex items-center mb-4">
                <div
                  className="w-20 h-20 rounded-3xl flex items-center justify-center mr-4 shadow-lg"
                  style={{ backgroundColor: `${riskProfile.color}20` }}
                >
                  <User size={40} style={{ color: riskProfile.color }} />
                </div>
                <div>
                  <h2
                    className="text-3xl font-bold mb-1"
                    style={{ color: COLORS.primary }}
                  >
                    {riskProfile.title}
                  </h2>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {riskProfile.description}
              </p>
            </div>

            {/* Key Metrics */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Expected Returns
                    </p>
                    <p
                      className="text-2xl font-bold"
                      style={{ color: riskProfile.color }}
                    >
                      {riskProfile.expectedReturns.min}% -{' '}
                      {riskProfile.expectedReturns.max}%
                    </p>
                  </div>
                  <TrendingUp size={32} style={{ color: riskProfile.color }} />
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Timeline
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {riskProfile.timeHorizon}
                    </p>
                  </div>
                  <Shield size={32} style={{ color: riskProfile.color }} />
                </div>
              </div>
            </div>
          </div>

          {/* Conservative Override Explanation */}
          {riskProfile.conservativeReasons &&
            riskProfile.conservativeReasons.length > 0 && (
              <div className="mt-8 p-6 bg-amber-50 border-2 border-amber-200 rounded-2xl">
                <div className="flex items-start">
                  <AlertTriangle
                    size={24}
                    className="text-amber-600 mr-4 mt-1 flex-shrink-0"
                  />
                  <div>
                    <h4 className="font-bold text-amber-800 mb-3 text-lg">
                      Why you're classified as Conservative
                    </h4>
                    <p className="text-amber-700 mb-4">
                      Despite some aggressive answers, certain critical factors
                      require a conservative approach:
                    </p>
                    <ul className="space-y-2 text-amber-700">
                      {riskProfile.conservativeReasons.map((reason, index) => (
                        <li key={index} className="flex items-start">
                          <span className="mr-3 mt-1">•</span>
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-amber-700 mt-4 font-semibold">
                      These factors override other preferences to protect your
                      financial well-being.
                    </p>
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Portfolio & Characteristics Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Portfolio Allocation */}
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
            <div className="flex items-center mb-8">
              <PieChart
                size={28}
                style={{ color: riskProfile.color }}
                className="mr-4"
              />
              <h3
                className="text-2xl font-bold"
                style={{ color: COLORS.primary }}
              >
                Portfolio allocation
              </h3>
            </div>

            <div className="space-y-6">
              {/* Equity */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="font-semibold text-gray-800 text-lg">
                    Equity funds
                  </span>
                  <span
                    className="text-2xl font-bold"
                    style={{ color: COLORS.accent }}
                  >
                    {riskProfile.recommendedAllocation.equity}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                  <div
                    className="h-4 rounded-full transition-all duration-500"
                    style={{
                      width: `${riskProfile.recommendedAllocation.equity}%`,
                      backgroundColor: COLORS.accent,
                    }}
                  ></div>
                </div>
                <p className="text-gray-600">
                  Growth potential with higher volatility
                </p>
              </div>

              {/* Debt */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="font-semibold text-gray-800 text-lg">
                    Debt funds
                  </span>
                  <span
                    className="text-2xl font-bold"
                    style={{ color: COLORS.sage }}
                  >
                    {riskProfile.recommendedAllocation.debt}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                  <div
                    className="h-4 rounded-full transition-all duration-500"
                    style={{
                      width: `${riskProfile.recommendedAllocation.debt}%`,
                      backgroundColor: COLORS.sage,
                    }}
                  ></div>
                </div>
                <p className="text-gray-600">Stability and regular income</p>
              </div>

              {/* Gold */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="font-semibold text-gray-800 text-lg">
                    Gold ETF
                  </span>
                  <span
                    className="text-2xl font-bold"
                    style={{ color: COLORS.gold }}
                  >
                    {riskProfile.recommendedAllocation.gold}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                  <div
                    className="h-4 rounded-full transition-all duration-500"
                    style={{
                      width: `${riskProfile.recommendedAllocation.gold}%`,
                      backgroundColor: COLORS.gold,
                    }}
                  ></div>
                </div>
                <p className="text-gray-600">Hedge against inflation</p>
              </div>
            </div>

            {/* Sample Portfolio */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-6">
                Sample portfolio (₹1 Lakh)
              </h4>
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="text-center p-2 sm:p-4 bg-blue-50 rounded-2xl">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">
                    Equity
                  </p>
                  <p
                    className="text-sm sm:text-xl font-bold break-words"
                    style={{ color: COLORS.accent }}
                  >
                    {formatCurrency(samplePortfolio.equity)}
                  </p>
                </div>
                <div className="text-center p-2 sm:p-4 bg-green-50 rounded-2xl">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">
                    Debt
                  </p>
                  <p
                    className="text-sm sm:text-xl font-bold break-words"
                    style={{ color: COLORS.sage }}
                  >
                    {formatCurrency(samplePortfolio.debt)}
                  </p>
                </div>
                <div className="text-center p-2 sm:p-4 bg-yellow-50 rounded-2xl">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">
                    Gold
                  </p>
                  <p
                    className="text-sm sm:text-xl font-bold break-words"
                    style={{ color: COLORS.gold }}
                  >
                    {formatCurrency(samplePortfolio.gold)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Your Characteristics */}
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
            <div className="flex items-center mb-8">
              <CheckCircle
                size={28}
                style={{ color: riskProfile.color }}
                className="mr-4"
              />
              <h3
                className="text-2xl font-bold"
                style={{ color: COLORS.primary }}
              >
                Your characteristics
              </h3>
            </div>

            <div className="space-y-4">
              {personalizedCharacteristics.map((characteristic, index) => (
                <div
                  key={index}
                  className="flex items-start p-4 bg-gray-50 rounded-xl"
                >
                  <CheckCircle
                    size={20}
                    style={{ color: riskProfile.color }}
                    className="mr-4 mt-0.5 flex-shrink-0"
                  />
                  <span className="text-gray-700 leading-relaxed">
                    {characteristic}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Detailed Answer Breakdown */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mt-12">
          <div className="flex items-center mb-6">
            <FileText
              size={24}
              style={{ color: riskProfile.color }}
              className="mr-3"
            />
            <h3
              className="text-xl font-semibold"
              style={{ color: COLORS.primary }}
            >
              Your assessment breakdown
            </h3>
          </div>

          <div className="space-y-6">
            {questions.map((question, index) => {
              const userAnswer = answers[question.id];
              const selectedOption = question.options.find(
                opt => opt.value === userAnswer
              );

              if (!selectedOption) return null;

              return (
                <div
                  key={question.id}
                  className="p-4 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start justify-between mb-4">
                    <p className="font-semibold text-md text-gray-900 flex-grow pr-4">
                      {index + 1}. {question.question}
                    </p>
                    <div className="flex items-center flex-shrink-0">
                      <button
                        onClick={() => setEditingQuestion(question.id)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors focus:outline-none focus:ring-0"
                        title="Edit answer"
                      >
                        <Edit3 size={16} />
                      </button>
                    </div>
                  </div>

                  {editingQuestion === question.id ? (
                    // Edit mode - show all options
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-gray-700 mb-3">
                        Choose a different answer:
                      </p>
                      {question.options.map(option => {
                        const isCurrentSelection =
                          answers[question.id] === option.value;

                        return (
                          <div
                            key={option.value}
                            className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                              isCurrentSelection
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                            }`}
                            onClick={() => {
                              handleAnswerChange(question.id, option.value);
                              setEditingQuestion(null);
                            }}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 mt-1">
                                <div
                                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                    isCurrentSelection
                                      ? 'border-blue-500 bg-blue-500'
                                      : 'border-gray-300 bg-white'
                                  }`}
                                >
                                  {isCurrentSelection && (
                                    <div className="w-1.5 h-1.5 bg-white rounded-full" />
                                  )}
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm text-gray-900 mb-1">
                                  {option.label}
                                </h4>
                                <p className="text-xs italic text-gray-500">
                                  {option.description}
                                </p>
                                <div className="flex items-center space-x-2 mt-2">
                                  <span className="text-xs text-gray-500">
                                    Score:
                                  </span>
                                  <RiskMeter
                                    riskLevel={option.score}
                                    size="sm"
                                    showLabel={true}
                                    showWeight={false}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="flex justify-end mt-3">
                        <button
                          onClick={() => setEditingQuestion(null)}
                          className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View mode - show selected answer
                    <div className="flex items-start space-x-3">
                      <CheckCircle
                        size={16}
                        className="text-green-600 mt-1 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-gray-800 mb-2">
                          {selectedOption.label}
                        </p>
                        <p className="text-sm text-gray-500 italic mb-3">
                          {selectedOption.description}
                        </p>
                        <div className="flex items-center space-x-3">
                          <span className="text-xs font-medium text-gray-500 min-w-0 flex-shrink-0">
                            Score:
                          </span>
                          <RiskMeter
                            riskLevel={selectedOption.score}
                            size="sm"
                            showLabel={true}
                            showWeight={false}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
          <Button
            onClick={startNewAssessment}
            size="md"
            variant="accent"
            className="min-w-[180px]"
          >
            Start new assessment
          </Button>
          <Button
            href="/tools/goal-planner"
            variant="ghost"
            size="md"
            className="min-w-[180px]"
          >
            Plan your goals
          </Button>
        </div>

        {/* Risk Disclaimer */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6 shadow-lg mt-12">
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle size={24} className="text-amber-600" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-bold text-amber-800 mb-3">
                Disclaimer
              </h4>
              <div className="text-sm text-amber-700 space-y-3">
                <p>
                  This risk assessment is provided for educational purposes only
                  and should not be considered as personalized investment
                  advice. The results are based on your responses and general
                  investment principles.
                </p>
                <p>
                  <strong>Please note:</strong>
                </p>
                <ul className="space-y-1 ml-4">
                  <li>
                    • Your actual risk tolerance may vary based on changing life
                    circumstances
                  </li>
                  <li>• Past performance does not guarantee future results</li>
                  <li>
                    • Investment returns are subject to market risks and
                    volatility
                  </li>
                  <li>
                    • Portfolio allocations are general recommendations, not
                    specific advice
                  </li>
                </ul>
                <p>
                  <strong>
                    Always consult with a qualified financial advisor
                  </strong>{' '}
                  before making investment decisions. Consider your complete
                  financial situation, goals, and risk tolerance before
                  investing.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Educational Content */}
        <motion.div
          className="mt-16 max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.2 }}
        >
          <article className="prose prose-lg md:prose-xl max-w-none">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-8">
              Understanding risk profiles
            </h2>

            <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
              Your risk profile is like your investment personality. It
              determines how much market volatility you can handle and what kind
              of returns you can expect. Think of it as choosing between a
              steady job and starting a business.
            </p>

            <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
              The five risk profiles explained
            </h3>

            <div className="space-y-6 mb-8 md:mb-10">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  Conservative investor
                </h4>
                <p className="text-lg text-gray-700 mb-3">
                  Like keeping money in a bank fixed deposit, with maximum
                  safety and limited growth. Perfect if you need money very soon
                  or cannot afford any losses.
                </p>
                <p className="text-gray-600">
                  <strong>Best for:</strong> Retirees, emergency funds, goals
                  within 1-2 years
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  Cautious investor
                </h4>
                <p className="text-lg text-gray-700 mb-3">
                  Like having a stable job with small side income, prioritizes
                  safety while allowing some growth potential. Comfortable with
                  minimal volatility.
                </p>
                <p className="text-gray-600">
                  <strong>Best for:</strong> Near-retirees, short to medium-term
                  goals, first-time investors
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  Balanced investor
                </h4>
                <p className="text-lg text-gray-700 mb-3">
                  Like having both a job and some business ventures, seeks
                  balance between growth and stability. Can handle moderate
                  market fluctuations.
                </p>
                <p className="text-gray-600">
                  <strong>Best for:</strong> Most working professionals,
                  medium-term goals, balanced approach seekers
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  Growth-focused investor
                </h4>
                <p className="text-lg text-gray-700 mb-3">
                  Like running your own successful business, prioritizes growth
                  over safety. Comfortable with significant market ups and downs
                  for better returns.
                </p>
                <p className="text-gray-600">
                  <strong>Best for:</strong> Young professionals, long-term
                  wealth building, experienced investors
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  Aggressive investor
                </h4>
                <p className="text-lg text-gray-700 mb-3">
                  Like being a serial entrepreneur, maximum growth potential
                  with highest volatility. Requires strong nerves and long-term
                  commitment.
                </p>
                <p className="text-gray-600">
                  <strong>Best for:</strong> Young investors with long horizons,
                  wealth maximization goals, high risk tolerance
                </p>
              </div>
            </div>

            <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
              How your risk score is calculated
            </h3>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              This risk profiler uses a sophisticated weighted scoring system
              that considers multiple factors with different levels of
              importance. Critical factors like your age and investment timeline
              carry more weight in determining your final profile.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8 md:mb-10">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  High-impact factors (Higher weight)
                </h4>
                <ul className="space-y-2 text-gray-700 list-disc list-inside pl-4">
                  <li>
                    <strong>Age & Life stage:</strong> Determines your time
                    horizon and recovery capacity
                  </li>
                  <li>
                    <strong>Investment timeline:</strong> Critical for risk
                    tolerance and goal alignment
                  </li>
                  <li>
                    <strong>Market reaction:</strong> Shows your emotional
                    response to volatility
                  </li>
                  <li>
                    <strong>Investment priority:</strong> Reveals your core
                    investment philosophy
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  Supporting factors (Standard weight)
                </h4>
                <ul className="space-y-2 text-gray-700 list-disc list-inside pl-4">
                  <li>
                    <strong>Income level:</strong> Affects your capacity to take
                    risks
                  </li>
                  <li>
                    <strong>Dependents:</strong> More responsibilities = need
                    stability
                  </li>
                  <li>
                    <strong>Emergency fund:</strong> Safety net enables higher
                    risk-taking
                  </li>
                  <li>
                    <strong>Debt situation:</strong> High debt requires
                    conservative approach
                  </li>
                  <li>
                    <strong>Investment experience:</strong> Knowledge builds
                    confidence
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 mb-8">
              <h4 className="text-lg font-semibold text-blue-900 mb-3">
                Smart override protection
              </h4>
              <p className="text-blue-800">
                This risk profiler also includes intelligent safeguards that may
                classify you as conservative even with aggressive answers if
                critical risk factors are present. For example, short investment
                timelines combined with panic-selling tendencies will trigger
                conservative recommendations to protect your financial goals.
              </p>
            </div>

            <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
              Asset allocation made simple
            </h3>

            <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
              Asset allocation is like creating a balanced meal, you need
              different ingredients in the right proportions:
            </p>

            <div className="space-y-4 mb-8 md:mb-10">
              <p className="text-lg text-gray-700">
                <strong>Equity (stocks/equity funds):</strong> Like the main
                course. Provides growth but can be volatile. Higher percentage
                for aggressive investors.
              </p>
              <p className="text-lg text-gray-700">
                <strong>Debt (bonds/debt funds):</strong> Like
                rice/roti/chapati, provides stability and steady returns. Higher
                percentage for conservative investors.
              </p>
              <p className="text-lg text-gray-700">
                <strong>Gold:</strong> Like chutney/pickle, a small portion that
                adds flavor and protects against inflation. Usually 5-10% for
                all investors.
              </p>
            </div>

            <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
              When to review your risk profile
            </h3>

            <p className="text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              Your risk profile isn't permanent. It changes with life events:
            </p>

            <ul className="space-y-3 text-lg text-gray-700 mb-8 md:mb-10 list-disc list-inside pl-4">
              <li>
                <strong>Job change:</strong> New income level may change your
                capacity
              </li>
              <li>
                <strong>Marriage/children:</strong> More responsibilities may
                require stability
              </li>
              <li>
                <strong>Approaching goals:</strong> Reduce risk as you near your
                target date
              </li>
              <li>
                <strong>Market experience:</strong> Your comfort level may
                change over time
              </li>
              <li>
                <strong>Age milestones:</strong> Review every 5 years or major
                life events
              </li>
            </ul>

            <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
              Risk and return go hand in hand
            </h3>

            <p className="text-lg text-gray-700 leading-relaxed">
              Higher potential returns always come with higher risk. The key is
              finding the right balance for your situation. Don't chase returns
              without understanding the risks, and don't be so conservative that
              inflation eats away your wealth.
            </p>
          </article>
        </motion.div>
      </ToolLayout>
    );
  }

  return (
    <ToolLayout
      title={`Risk profiler | ${CONTACT_INFO.company.name}`}
      heroTitle="Risk profiler"
      description="Assess your risk tolerance and get personalized investment recommendations based on your financial profile"
      canonicalUrl="https://niveshright.com/tools/risk-profiler"
      backgroundColor={COLORS.mist}
      heroStyle="gradient"
      heroIcon={
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${COLORS.gold}20` }}
        >
          <Shield size={32} style={{ color: COLORS.gold }} />
        </div>
      }
      showBreadcrumb={true}
      keywords="risk profiler, investment risk assessment, portfolio allocation, risk tolerance, investment personality"
      cta={
        <WhatsAppCTA
          title="Need help with risk assessment?"
          description="Get personalized guidance on understanding your risk profile and building the right investment portfolio."
          phoneNumber={CONTACT_INFO.phone.whatsapp}
          message={
            answeredQuestions > 0
              ? `Hi ${CONTACT_INFO.contact_person.short_name}! I started your risk profiler assessment and answered ${answeredQuestions} out of ${questions.length} questions. I'd like help understanding my risk profile and getting personalized investment advice. Can you guide me?`
              : `Hi ${CONTACT_INFO.contact_person.short_name}! I'm interested in understanding my investment risk profile and getting personalized portfolio recommendations. Can you help me assess my risk tolerance and suggest the right investment strategy?`
          }
          buttonText="Get risk assessment help"
          backgroundColor={COLORS.pearl}
          buttonColor={COLORS.gold}
        />
      }
    >
      <div className="max-w-4xl mx-auto">
        {/* Progress Bar */}
        <motion.div
          className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex justify-between items-center mb-4">
            <h2
              className="text-xl font-semibold"
              style={{ color: COLORS.primary }}
            >
              Risk profiler questionnaire
            </h2>
            <span className="text-sm" style={{ color: COLORS.smoke }}>
              {answeredQuestions} of {questions.length} answered (
              {Math.round(progress)}%)
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <motion.div
              className="h-3 rounded-full"
              style={{ backgroundColor: COLORS.accent }}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            ></motion.div>
          </div>
        </motion.div>

        {/* All Questions */}
        <div className="space-y-8">
          {questions.map((question, index) => (
            <motion.div
              key={question.id}
              className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <h3 className="text-lg sm:text-xl font-semibold mb-4">
                <QuestionBadge
                  number={index + 1}
                  isAnswered={!!answers[question.id]}
                />
                <span style={{ color: COLORS.primary }}>
                  {question.question}
                </span>
              </h3>

              <div className="space-y-3">
                {question.options.map(option => {
                  const isSelected = answers[question.id] === option.value;

                  return (
                    <div
                      key={option.value}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() =>
                        handleAnswerChange(question.id, option.value)
                      }
                    >
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 mt-1">
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              isSelected
                                ? 'border-blue-500 bg-blue-500'
                                : 'border-gray-300 bg-white'
                            }`}
                          >
                            {isSelected && (
                              <div className="w-2 h-2 bg-white rounded-full" />
                            )}
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 mb-2">
                            {option.label}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {option.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Submit Button */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Button
            onClick={handleSubmit}
            variant="accent"
            size="md"
            disabled={!allQuestionsAnswered}
            className="min-w-[200px]"
          >
            {allQuestionsAnswered
              ? 'Get my risk profile'
              : `Answer ${questions.length - answeredQuestions} more questions`}
          </Button>

          {answeredQuestions > 0 && (
            <Button
              onClick={startNewAssessment}
              variant="ghost"
              size="md"
              className="min-w-[180px]"
            >
              Clear all answers
            </Button>
          )}
        </motion.div>
      </div>
    </ToolLayout>
  );
};
